import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useBranding } from "../lib/branding";
import { isPasswordValid, PASSWORD_MIN_LEN } from "../lib/passwordPolicy";
import PasswordStrengthChecklist from "../components/PasswordStrengthChecklist";

export default function SetupPassword() {
  const branding = useBranding();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [invite, setInvite] = useState(null);   // { email, first_name, tenant_name }
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [inviteErr, setInviteErr] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) { setInviteErr("This invite link is missing its token."); setLoadingInvite(false); return; }
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setInvite(data);
      } catch (e) {
        setInviteErr(formatApiErrorDetail(e.response?.data?.detail) || "This invite link is no longer valid");
      }
      setLoadingInvite(false);
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!isPasswordValid(password)) {
      setErr(`Password must be at least ${PASSWORD_MIN_LEN} characters and include a letter, a number, and a symbol.`);
      return;
    }
    if (password !== confirm) { setErr("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/setup-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not set your password");
    }
    setSaving(false);
  };

  return (
    <div className="auth-split" data-testid="setup-shell">
      <div className="auth-split-left">
        <div className="auth-split-logo">
          <img src={branding.logo_data_url || "/pragma-login-logo-v4.png"} alt="Pragma" />
        </div>

        <div className="auth-split-form-wrap">
          {loadingInvite ? (
            <>
              <h1 className="auth-split-h1">Loading your invite…</h1>
            </>
          ) : inviteErr ? (
            <>
              <h1 className="auth-split-h1" data-testid="setup-invalid-heading">This invite is no longer valid</h1>
              <p className="auth-split-sub" data-testid="setup-invalid-body">{inviteErr}</p>
              <div className="auth-split-actions">
                <Link to="/login" className="auth-split-submit">
                  Go to sign in <ArrowRight strokeWidth={2.2} />
                </Link>
              </div>
            </>
          ) : done ? (
            <>
              <h1 className="auth-split-h1" data-testid="setup-done-heading">
                <CheckCircle2 size={24} style={{ display: "inline", verticalAlign: -3, color: "var(--pg-lime, #34a853)", marginRight: 8 }} />
                You&apos;re all set
              </h1>
              <p className="auth-split-sub">Sending you to sign in…</p>
            </>
          ) : (
            <>
              <span className="auth-card-badge" style={{ marginBottom: 14 }}>SET YOUR PASSWORD</span>
              <h1 className="auth-split-h1" data-testid="setup-heading">
                Welcome, {invite.first_name || invite.email}
              </h1>
              <p className="auth-split-sub">
                You&apos;ve been added to <strong>{invite.tenant_name}</strong>.
                Pick a strong password to activate your account.
              </p>

              <form onSubmit={submit} data-testid="setup-form">
                <input
                  type="email" value={invite.email} readOnly
                  className="auth-split-input" style={{ opacity: 0.6, cursor: "not-allowed" }}
                  data-testid="setup-email"
                />
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="auth-split-input" placeholder="New password"
                  data-testid="setup-password" required autoComplete="new-password" minLength={PASSWORD_MIN_LEN}
                />
                <PasswordStrengthChecklist password={password} testid="setup-password-rules" />
                <input
                  type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="auth-split-input" placeholder="Confirm password"
                  data-testid="setup-confirm" required autoComplete="new-password" minLength={PASSWORD_MIN_LEN}
                />
                {err && <div className="auth-split-error" data-testid="setup-error">{err}</div>}
                <div className="auth-split-actions" style={{ justifyContent: "flex-end" }}>
                  <button type="submit" className="auth-split-submit" disabled={saving} data-testid="setup-submit">
                    {saving ? "Setting up…" : "Set password & continue"}
                    <ArrowRight strokeWidth={2.2} />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      <div className="auth-split-right">
        <h2 className="auth-split-right-h1">
          {branding.heading_line_1}<br /><span className="accent">{branding.heading_line_2}</span>
        </h2>
      </div>
    </div>
  );
}
